/**
 * Service Layer para API de Meus Anúncios
 * Base path: /ad/v1/{vehicleType}
 *
 * Co-localizado com hooks seguindo arquitetura ANCR-FA.
 * Zero lógica de negócio — puro HTTP.
 */

import { httpClient } from '@/services/api';
import { ENV } from '@/app/config';
import type {
	TVehicleType,
	TAdListParams,
	TAdsApiResponse,
	TAdsListResponse,
	TAdListItem,
	TAdDetail,
	TAdStatus,
	TToggleAdStatusResponse,
	TUpdateVehiclePayload,
	TUpdateVehicleResponse,
	TUpdateCharacteristicsPayload,
	TVehicleCharacteristics,
	TRequestUpgradePayload,
	TRequestUpgradeResponse,
	TDeleteIncompleteAdResponse
} from './ads-listing.types';

const getBaseUrl = (vehicleType: TVehicleType): string => {
	return `ad/v1/${vehicleType}`;
};

// Card thumbnails: 391x279 (mesma query string usada na legacy garagem).
const CARD_PHOTO_QUERY = '?s=fill&w=391&h=279&q=70&t=true';

// API retorna o caminho relativo da imagem no campo `imageFile` com separador
// `\` (estilo Windows). Compõe a URL final preservando o contrato `photoUrl`
// exposto pelos componentes.
const composePhotoUrl = (imageFile: string | undefined | null): string | undefined => {
	if (!imageFile) return undefined;
	const path = imageFile.replace(/\\/g, '/');
	return `${ENV.API_IMAGE_URL}${path}${CARD_PHOTO_QUERY}`;
};

const mapAdListItem = (
	raw: TAdListItem & { imageFile?: string | null },
	vehicleType: TVehicleType
): TAdListItem => {
	const withType = raw.vehicleType ? raw : { ...raw, vehicleType };
	if (withType.photoUrl) return withType;
	const photoUrl = composePhotoUrl(withType.imageFile);
	return photoUrl ? { ...withType, photoUrl } : withType;
};

type TAdPhotoSource = TAdDetail & {
	imageFile?: string | null;
	urlFotos?: string | null;
	UrlFotos?: string | null;
	price?: number;
	Price?: number;
	Preco?: number;
	Kilometers?: number;
	Quilometragem?: number;
	Description?: string;
	Observacoes?: string;
};

const resolvePhotoUrl = (raw: TAdPhotoSource): string | undefined => {
	if (raw.photoUrl) return raw.photoUrl;
	const fromImageFile = composePhotoUrl(raw.imageFile);
	if (fromImageFile) return fromImageFile;
	if (raw.urlFotos) return raw.urlFotos;
	if (raw.UrlFotos) return raw.UrlFotos;
	return undefined;
};

const mapAdDetail = (raw: TAdPhotoSource): TAdDetail => {
	const photoUrl = resolvePhotoUrl(raw);
	const salePrice = raw.salePrice ?? raw.price ?? raw.Price ?? raw.Preco ?? 0;
	const kilometers = raw.kilometers ?? raw.Kilometers ?? raw.Quilometragem ?? 0;
	const description = raw.description ?? raw.Description ?? raw.Observacoes ?? '';

	const mapped: TAdDetail = {
		...raw,
		salePrice,
		kilometers,
		description,
		...(photoUrl ? { photoUrl } : {})
	};

	return mapped;
};

const adsListingService = {
	/**
	 * GET /ad/v1/{vehicleType}
	 * API retorna { meta, data: { "Ativos": { adverts, count, totalCount }, ... } }
	 * Normaliza para { ads, count, totalCount } extraindo do primeiro grupo retornado
	 */
	listAds: async (vehicleType: TVehicleType, params?: TAdListParams): Promise<TAdsListResponse> => {
		const { data: response } = await httpClient.get<TAdsApiResponse>(getBaseUrl(vehicleType), { params });
		const firstGroup = Object.values(response.data)[0];
		const adverts = firstGroup?.adverts ?? [];
		return {
			ads: adverts.map((ad) => mapAdListItem(ad, vehicleType)),
			count: firstGroup?.count ?? 0,
			totalCount: firstGroup?.totalCount ?? 0
		};
	},

	/**
	 * GET /ad/v1/{vehicleType}/{id}
	 * Busca detalhes de um anúncio específico
	 */
	getAdDetail: async (vehicleType: TVehicleType, id: number): Promise<TAdDetail> => {
		const { data } = await httpClient.get(`${getBaseUrl(vehicleType)}/${id}`);
		const detail = ((data as Record<string, unknown>).data as TAdDetail) || (data as TAdDetail);
		return mapAdDetail(detail);
	},

	/**
	 * PATCH /ad/v1/{vehicleType}/{id}/status
	 * Alterna status do anúncio (ativo/inativo)
	 */
	toggleAdStatus: async (
		vehicleType: TVehicleType,
		id: number,
		status: TAdStatus
	): Promise<TToggleAdStatusResponse> => {
		const { data } = await httpClient.patch<TToggleAdStatusResponse>(
			`${getBaseUrl(vehicleType)}/${id}/status`,
			{ status }
		);
		return data;
	},

	/**
	 * PATCH /ad/v1/{vehicleType}/{id}/vehicle
	 * Atualiza informações do veículo (ANCR-4596)
	 */
	updateVehicle: async (
		vehicleType: TVehicleType,
		id: number,
		payload: TUpdateVehiclePayload
	): Promise<TUpdateVehicleResponse> => {
		const { data } = await httpClient.patch<TUpdateVehicleResponse>(
			`${getBaseUrl(vehicleType)}/${id}/vehicle`,
			payload
		);
		return data;
	},

	/**
	 * PATCH /ad/v1/{vehicleType}/{id}/characteristics
	 * Atualiza opcionais e condições do veículo
	 */
	updateCharacteristics: async (
		vehicleType: TVehicleType,
		id: number,
		payload: TUpdateCharacteristicsPayload
	): Promise<Record<string, unknown>> => {
		const { data } = await httpClient.patch(
			`${getBaseUrl(vehicleType)}/${id}/characteristics`,
			payload
		);
		return data as Record<string, unknown>;
	},

	/**
	 * GET /ad/v1/{vehicleType}/characteristics
	 * Lista condições do veículo (ANCR-4597)
	 */
	getCharacteristics: async (vehicleType: TVehicleType): Promise<TVehicleCharacteristics> => {
		const { data } = await httpClient.get(`${getBaseUrl(vehicleType)}/characteristics`);
		return (data as Record<string, unknown>).data as TVehicleCharacteristics || data as TVehicleCharacteristics;
	},

	/**
	 * GET /ad/v1/{vehicleType}/characteristics/optional
	 * Lista opcionais disponíveis para o veículo
	 */
	getOptionals: async (vehicleType: TVehicleType): Promise<TVehicleCharacteristics> => {
		const { data } = await httpClient.get(`${getBaseUrl(vehicleType)}/characteristics/optional`);
		return (data as Record<string, unknown>).data as TVehicleCharacteristics || data as TVehicleCharacteristics;
	},

	/**
	 * POST /ad/v1/{vehicleType}/{id}/upgrade
	 * Solicita upgrade de plano para um anúncio (ANCR-4598)
	 */
	requestUpgrade: async (
		vehicleType: TVehicleType,
		id: number,
		payload: TRequestUpgradePayload
	): Promise<TRequestUpgradeResponse> => {
		const { data } = await httpClient.post<TRequestUpgradeResponse>(
			`${getBaseUrl(vehicleType)}/${id}/upgrade`,
			payload
		);
		return data;
	},

	/**
	 * DELETE /ad/v1/{vehicleType}/{id}
	 * Exclui um anúncio incompleto (equivale ao excluir-anuncio-incompleto da garagem).
	 */
	deleteIncompleteAd: async (
		vehicleType: TVehicleType,
		id: number
	): Promise<TDeleteIncompleteAdResponse> => {
		const { data } = await httpClient.delete<TDeleteIncompleteAdResponse>(
			`${getBaseUrl(vehicleType)}/${id}`
		);
		return data;
	}
};

export { adsListingService };
